import { getTriageCurrentUser, getTriageCurrentUserId, getTriageStaff } from './identity';
import { TI_STATUSES } from './config';
import type {
  TriageAssigneeSelection,
  TriageChannelFilter,
  TriageHeaderStats,
  TriageItem,
  TriageSegment,
  TriageStaff,
  TriageStatusSelection,
  TriageSuggestedAction,
} from './types';

export function tiItemKind(item: TriageItem): TriageSegment {
  return item.itemKind ?? 'attention';
}

export function tiStaffById(id: string | null | undefined): TriageStaff | null {
  if (!id) return null;
  return getTriageStaff().find((s) => s.id === id) ?? null;
}

export function tiCurrentUser(): TriageStaff {
  return getTriageCurrentUser();
}

export function triageHeaderStats(items: TriageItem[] = []): TriageHeaderStats {
  const openItems = items.filter((i) => i.status !== 'closed');
  const currentUserId = getTriageCurrentUserId();
  return {
    mine: openItems.filter((i) => i.assigneeId === currentUserId).length,
    active: openItems.length,
    urgent: items.filter((i) => i.status === 'open' && i.priority === 'urgent').length,
    attention: items.filter((i) => tiItemKind(i) === 'attention' && i.status !== 'closed').length,
    callTasks: items.filter((i) => tiItemKind(i) === 'call_task' && i.status !== 'closed').length,
  };
}

export interface TriageFilterParams {
  items: TriageItem[];
  segment: TriageSegment;
  /**
   * Which agent sent the work here — `?source=recall` on the URL (amendment 24
   * §7). An agent page linking to Triage must land on **its own** queue, not on
   * everybody's; the recall page's *7 calls waiting* was counting something
   * Triage never showed filtered.
   */
  source?: string | undefined;
  statusSelection: TriageStatusSelection;
  channelFilter: TriageChannelFilter;
  assigneeSelection: TriageAssigneeSelection;
  searchQuery: string;
}

export function tiStatusLabel(selection: TriageStatusSelection): string {
  if (!selection.length) return 'All statuses';
  const first = TI_STATUSES[selection[0]!]?.label ?? selection[0]!;
  return selection.length > 1 ? `${first} +${selection.length - 1}` : first;
}

export function tiAssigneeLabel(selection: TriageAssigneeSelection): string {
  if (!selection.length) return 'Anyone';
  const labelOne = (s: string) =>
    s === 'mine'
      ? 'Mine'
      : s === 'unassigned'
        ? 'Unassigned'
        : (tiStaffById(s)?.name.split(' ')[0] ?? 'Staff');
  const first = labelOne(selection[0]!);
  return selection.length > 1 ? `${first} +${selection.length - 1}` : first;
}

export function tiToggleSelection<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
}

export function filterTriageItems({
  items,
  segment,
  source,
  statusSelection,
  channelFilter,
  assigneeSelection,
  searchQuery,
}: TriageFilterParams): TriageItem[] {
  return items.filter((item) => {
    if (tiItemKind(item) !== segment) return false;
    if (source !== undefined && item.triggeredBy !== source) return false;
    if (statusSelection.length > 0 && !statusSelection.includes(item.status)) return false;
    if (channelFilter !== 'All' && item.channel !== channelFilter) return false;
    if (assigneeSelection.length > 0) {
      const currentUserId = getTriageCurrentUserId();
      const match = assigneeSelection.some((sel) =>
        sel === 'mine'
          ? item.assigneeId === currentUserId
          : sel === 'unassigned'
            ? item.assigneeId === null
            : item.assigneeId === sel,
      );
      if (!match) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !item.patient.toLowerCase().includes(q) &&
        !item.subject.toLowerCase().includes(q) &&
        !(item.aiSummary ?? '').toLowerCase().includes(q) &&
        !item.tags.some((tg) => tg.toLowerCase().includes(q))
      ) {
        return false;
      }
    }
    return true;
  });
}

export function applyTriageAction(
  items: TriageItem[],
  itemId: number,
  actionId: string,
  editedBody?: string,
): TriageItem[] {
  type SpawnPayload = {
    template: NonNullable<TriageSuggestedAction['spawn']>;
    source: TriageItem;
  };

  let spawnPayload: SpawnPayload | undefined;

  const next = items.map((i) => {
    if (i.id !== itemId) return i;
    const action = (i.suggestedActions ?? []).find((a) => a.id === actionId);
    if (!action) return i;

    const actions = (i.suggestedActions ?? []).map((a) =>
      a.id === actionId ? { ...a, done: true } : a,
    );

    let receipt = `${action.label} — done.`;
    const patch: Partial<TriageItem> = {};

    switch (action.type) {
      case 'send_sms':
        receipt = `Sent SMS to ${i.patient}: "${(editedBody || action.body || '').slice(0, 90)}…"`;
        break;
      case 'send_email':
        receipt = `Sent email to ${i.patient}.`;
        break;
      case 'book_appointment':
        receipt = `Booked: ${action.slot || action.label}.`;
        break;
      case 'escalate':
        patch.assigneeId = action.target ?? i.assigneeId;
        receipt = `Escalated to ${tiStaffById(action.target)?.name || 'team'}.`;
        break;
      case 'notify_provider': {
        const statusCfg = TI_STATUSES[i.status];
        patch.status = statusCfg?.canTransitionTo.includes('waiting') ? 'waiting' : i.status;
        receipt = `Notified ${tiStaffById(action.target)?.name || 'provider'}. Waiting on their action.`;
        break;
      }
      case 'add_note':
        receipt = action.note || 'Note added to chart.';
        break;
      case 'flag_internal':
        receipt = `Flagged internally — ${action.note || action.label}.`;
        break;
      case 'resume_recall':
        receipt = action.note || 'Recall scheduled to resume.';
        break;
      case 'create_call_task':
        receipt = 'Created a call task — see "Calls to make".';
        if (action.spawn) spawnPayload = { template: action.spawn, source: i };
        break;
      default:
        break;
    }

    return {
      ...i,
      suggestedActions: actions,
      ...patch,
      notes: [
        ...i.notes,
        { author: tiCurrentUser().name, time: 'Just now', text: `Elva action — ${receipt}` },
      ],
    };
  });

  if (spawnPayload !== undefined) {
    const { template, source } = spawnPayload;
    const maxId = items.reduce((m, x) => Math.max(m, x.id), 0);
    const spawned: TriageItem = {
      id: maxId + 1,
      patient: source.patient,
      itemKind: 'call_task',
      channel: 'none',
      goalType: template.goalType,
      priority: source.priority,
      time: 'Just now',
      subject: template.subject,
      status: 'open',
      assigneeId: getTriageCurrentUserId(),
      callAttempts: 0,
      sourceLabel: 'Created from Triage',
      sourceType: 'triage_call_task',
      callTask: {
        recallName: `From triage · ${source.patient}`,
        script: template.script,
        contextCard: template.contextCard ?? [],
        priorAttempts: [],
      },
      notes: [],
      tags: ['From triage'],
      ...(source.patientInfo ? { patientInfo: source.patientInfo } : {}),
    };
    next.push(spawned);
  }

  return next;
}
